import React from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  CalendarIcon,
  LightningIcon,
  TargetIcon,
  TrophyIcon,
} from "@phosphor-icons/react";
import CustomTable from "../../../shared/CustomTable/CustomTable";
import { CustomBadge } from "../../../shared/CustomBadge/CustomBadge";
import EmptyContainer from "../../../shared/EmptyContainer/EmptyContainer";
import { IExerciseHistoryTestResult } from "../../../endpoints/profiling";
import styles from "../AthleteExerciseHistoryPage.module.scss";

interface TestingHistoryTabProps {
  testResults: IExerciseHistoryTestResult[];
  isLoading: boolean;
}

function formatDate(d?: string): string {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    });
  } catch {
    return d;
  }
}

/** Tooltip trend (sez.9.2 — grafico andamento testing nel tempo). */
const TrendTooltip: React.FC<any> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className={styles.trendTooltip}>
      <p className={styles.trendTooltipDate}>{label}</p>
      {payload.map((p: any) => (
        <div key={p.dataKey}>
          {p.value} {p.payload?.unit ?? ""}
        </div>
      ))}
    </div>
  );
};

const TICK_STYLE = {
  fill: "#4d4d4d",
  fontSize: 11,
  fontFamily: "'Satoshi', sans-serif",
};

const TESTING_HEADERS = ["Data", "Protocollo", "Valore", "Source", "PR"] as const;

/**
 * Sezione Testing — sez.9.2:
 * - Grafico trend nel tempo
 * - Ultima misurazione evidenziata con metriche secondarie da values{}
 * - Tabella cronologica
 */
const TestingHistoryTab: React.FC<TestingHistoryTabProps> = ({
  testResults,
  isLoading,
}) => {
  if (!isLoading && testResults.length === 0) {
    return (
      <EmptyContainer
        Icon={TargetIcon}
        title="Nessun test eseguito"
        description="I risultati dei test appariranno qui quando verranno registrati."
        addButton={false}
        onClick={() => undefined}
        buttonText=""
      />
    );
  }

  /* Ultima misurazione (primo elemento — array in ordine desc dal server) */
  const latest = testResults[0] ?? null;

  /* Metriche secondarie dall'ultima misurazione (chiavi in values{} escludendo value primario) */
  const latestSecondary: Array<{ key: string; val: number }> = latest?.values
    ? Object.entries(latest.values)
        .filter(([, v]) => typeof v === "number")
        .slice(0, 5)
        .map(([k, v]) => ({ key: k, val: v as number }))
    : [];

  /* Dati grafico trend (ordine cronologico asc) */
  const chartData = [...testResults].reverse().map((tr) => ({
    date: formatDate(tr.date),
    value: tr.value,
    unit: tr.unit,
    isPR: tr.isPR,
  }));

  const tableData = testResults.map((tr) => ({
    _id: tr._id,
    date: formatDate(tr.date),
    protocolName: tr.protocolName || "—",
    value: `${tr.value} ${tr.unit}`,
    sourceType: tr.sourceType,
    isPR: tr.isPR,
  }));

  return (
    <div className={styles.tabPanel}>
      {/* Ultima misurazione con metriche secondarie (sez.9.2) */}
      {latest && (
        <div className={styles.latestMeasurementBar}>
          <div>
            <p className={styles.latestMeasurementLabel}>Ultima misurazione</p>
            <p className={styles.latestMeasurementValue}>
              {latest.value} {latest.unit}
            </p>
            <p className={styles.latestMeasurementSub}>
              {formatDate(latest.date)}
              {latest.isPR && " · PR"}
            </p>
          </div>

          {latestSecondary.length > 0 && (
            <div className={styles.latestMeasurementSecondary}>
              {latestSecondary.map(({ key, val }) => (
                <div key={key} className={styles.latestMetricItem}>
                  <p className={styles.latestMetricKey}>{key}</p>
                  <p className={styles.latestMetricVal}>
                    {typeof val === "number"
                      ? val % 1 === 0
                        ? val
                        : val.toFixed(3).replace(/\.?0+$/, "")
                      : val}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Grafico trend nel tempo (sez.9.2) */}
      {chartData.length > 1 && (
        <div className={styles.trendChartWrap}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{ top: 8, right: 24, bottom: 8, left: 8 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#e7e7e7"
                vertical={false}
              />
              <XAxis
                dataKey="date"
                tick={TICK_STYLE}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tick={TICK_STYLE}
                tickLine={false}
                axisLine={false}
                width={40}
              />
              <Tooltip content={<TrendTooltip />} />
              <Line
                type="monotone"
                dataKey="value"
                name="Valore"
                stroke="#063444"
                strokeWidth={2}
                dot={(props: any) => {
                  const { cx, cy, payload } = props;
                  return (
                    <circle
                      key={`dot-${cx}-${cy}`}
                      cx={cx}
                      cy={cy}
                      r={payload.isPR ? 6 : 4}
                      fill={payload.isPR ? "#ffd15a" : "#063444"}
                      stroke="none"
                    />
                  );
                }}
                activeDot={{ r: 6, fill: "#063444" }}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Intestazione tabella */}
      <div className={styles.testingHeader}>
        <TargetIcon size={16} weight="bold" color="#00212c" />
        <p className={styles.testingTitle}>Cronologia test (recenti prima)</p>
      </div>

      <CustomTable
        isLoading={isLoading}
        headers={[...TESTING_HEADERS]}
        data={tableData}
        fields={{
          Data: {
            key: "date",
            customComponent: (value) => (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                <CalendarIcon size={12} color="#b5b5b5" />
                {value}
              </span>
            ),
          },
          Protocollo: { key: "protocolName" },
          Valore: {
            key: "value",
            align: "end",
          },
          Source: {
            key: "sourceType",
            customComponent: (_value, record) => (
              <span className={styles.sourceCell}>
                {record.sourceType === "auto_detected_from_training" ? (
                  <CustomBadge label="Auto" color="lightblue" />
                ) : (
                  <CustomBadge label="Test" color="lightgreen" />
                )}
                {record.sourceType === "auto_detected_from_training" && (
                  <LightningIcon size={12} color="#4d4d4d" />
                )}
              </span>
            ),
          },
          PR: {
            key: "isPR",
            customComponent: (_value, record) =>
              record.isPR ? (
                <span className={styles.prCell}>
                  <TrophyIcon size={12} weight="fill" color="#ffd15a" />
                  <CustomBadge label="PR" color="lightgreen" />
                </span>
              ) : null,
          },
        }}
      />
    </div>
  );
};

export default TestingHistoryTab;
